// @djsem/shared — Track Relationship Graph / Graph Playlists (Prompt 10).
//
// A DJSEM-native, EXPLAINABLE track relationship graph for Track Library / Set Builder /
// Transition Designer / Prep Workspace. Every edge carries a score, reasons, a plain
// explanation, and warnings — no opaque recommender, no black box. Reuses the Prompt 07
// scoring philosophy (camelotCompatibility). Prep-side only: the live conductor consumes a
// chosen set/transition plan, never this exploration logic (product boundary).

import {
  vArray, vNumber, vObject, vString, vRefine, type Validator,
  type DJSEMValidationResult, toDJSEMValidationResult,
} from "./validation";
import { camelotCompatibility } from "./setFlowSorting";
import type {
  DJSEMTrackGraphNode, DJSEMTrackGraphEdge, DJSEMGraphEdgeReason,
} from "./showDirection";

// ---- contracts --------------------------------------------------------------

/** The kind of filter applied to a track relationship graph's nodes or edges. */
export type DJSEMTrackGraphFilterKind =
  | "approved_only" | "bpm_range" | "genre" | "mood" | "energy_range"
  | "semantic_domain" | "cue_intent" | "transition_risk" | "min_compatibility" | "chapter_fit";

/** A single filter applied when narrowing a {@link DJSEMTrackRelationshipGraph}. */
export interface DJSEMTrackGraphFilter {
  kind: DJSEMTrackGraphFilterKind;
  value?: unknown;
  min?: number;
  max?: number;
}

/** A DJ-specified manual link between two tracks, forced into the graph regardless of score. */
export interface DJSEMManualGraphLink {
  fromPreparationTrackId: string;
  toPreparationTrackId: string;
  note?: string;
}

/** Weights and options controlling how {@link TrackRelationshipGraphService.buildGraph} scores and prunes edges. */
export interface TrackGraphBuildOptions {
  bpmWeight: number;
  keyWeight: number;
  energyWeight: number;
  moodWeight: number;
  semanticWeight: number;
  approvalWeight: number;
  directed: boolean;
  minScoreForEdge: number;   // edges below this are dropped
  manualLinks?: DJSEMManualGraphLink[];
}

/** Default weights/options for building a track relationship graph. */
export const DEFAULT_GRAPH_OPTIONS: TrackGraphBuildOptions = {
  bpmWeight: 3, keyWeight: 2, energyWeight: 2, moodWeight: 1, semanticWeight: 2,
  approvalWeight: 1, directed: false, minScoreForEdge: 0.15, manualLinks: [],
};

/** An explainable, scored graph of tracks and their pairwise compatibility edges. */
export interface DJSEMTrackRelationshipGraph {
  id: string;
  createdAt: string;
  nodes: DJSEMTrackGraphNode[];
  edges: DJSEMTrackGraphEdge[];
  filters: DJSEMTrackGraphFilter[];
  warnings: string[];
}

// ---- validator --------------------------------------------------------------

/** Runtime validator for {@link DJSEMTrackRelationshipGraph}: rejects duplicate node ids, out-of-range edge scores, and edges referencing unknown nodes. */
export const vTrackRelationshipGraph: Validator<DJSEMTrackRelationshipGraph> = vRefine(
  vObject({
    id: vString({ min: 1 }), createdAt: vString({ min: 1 }),
    nodes: vArray(vObject({ preparationTrackId: vString({ min: 1 }), title: vString({ min: 1 }) }, {}, { open: true })),
    edges: vArray(vObject({
      id: vString({ min: 1 }), fromPreparationTrackId: vString({ min: 1 }), toPreparationTrackId: vString({ min: 1 }),
      compatibilityScore: vNumber({ min: 0, max: 1 }),
    }, {}, { open: true })),
    filters: vArray((_v) => ({ valid: true, issues: [] })),
    warnings: vArray(vString({})),
  }) as Validator<DJSEMTrackRelationshipGraph>,
  (g) => {
    const ids = new Set<string>();
    for (const n of g.nodes) {
      if (ids.has(n.preparationTrackId)) return `duplicate node id: ${n.preparationTrackId}`;
      ids.add(n.preparationTrackId);
    }
    for (const e of g.edges) {
      if (!(e.compatibilityScore >= 0 && e.compatibilityScore <= 1)) return `edge ${e.id} score out of range`;
      if (!ids.has(e.fromPreparationTrackId) || !ids.has(e.toPreparationTrackId)) return `edge ${e.id} references unknown node`;
    }
    return null;
  },
);

// ---- service (class-based; hard rule 12) ------------------------------------

const clampUnit = (n: number) => Math.max(0, Math.min(1, Number.isFinite(n) ? n : 0));
const round = (n: number) => Math.round(n * 1000) / 1000;

/** Builds, scores, and filters explainable track relationship graphs for the Track Library / Set Builder / Transition Designer. Prep-side only. */
export class TrackRelationshipGraphService {
  /**
   * Build a scored relationship graph over a set of tracks. Edges below
   * `options.minScoreForEdge` are dropped unless manually linked.
   * @param tracks - The tracks to include as nodes.
   * @param options - Weights and options controlling scoring and edge pruning.
   * @returns The resulting graph (deterministic node order by id).
   */
  buildGraph(tracks: DJSEMTrackGraphNode[], options: TrackGraphBuildOptions = DEFAULT_GRAPH_OPTIONS): DJSEMTrackRelationshipGraph {
    const warnings: string[] = [];
    // Deterministic node order by id.
    const nodes = [...tracks].sort((a, b) => a.preparationTrackId.localeCompare(b.preparationTrackId));
    const manual = new Set((options.manualLinks ?? []).map((l) => `${l.fromPreparationTrackId}->${l.toPreparationTrackId}`));

    const edges: DJSEMTrackGraphEdge[] = [];
    for (let i = 0; i < nodes.length; i++) {
      for (let j = 0; j < nodes.length; j++) {
        if (i === j) continue;
        // Undirected: only build the i<j half to avoid duplicate mirror edges.
        if (!options.directed && j < i) continue;
        const a = nodes[i], b = nodes[j];
        const edge = this.scoreEdge(a, b, options);
        const isManual = manual.has(`${a.preparationTrackId}->${b.preparationTrackId}`) || (!options.directed && manual.has(`${b.preparationTrackId}->${a.preparationTrackId}`));
        if (isManual && !edge.reasons.includes("manual_link")) {
          edge.reasons.push("manual_link");
          edge.explanation = `Manually linked. ${edge.explanation}`.trim();
        }
        if (edge.compatibilityScore >= options.minScoreForEdge || isManual) edges.push(edge);
      }
    }
    return {
      id: `graph_${nodes.length}n_${edges.length}e`,
      createdAt: "1970-01-01T00:00:00.000Z", // deterministic; caller may overwrite
      nodes, edges, filters: [], warnings,
    };
  }

  /**
   * Score a single edge between two tracks across BPM, key, energy, mood, semantic, and
   * approval dimensions, producing an explainable reason list and warnings.
   * @param from - The source track.
   * @param to - The target track.
   * @param options - Per-dimension weights and directedness.
   * @returns The scored, explained edge.
   */
  scoreEdge(from: DJSEMTrackGraphNode, to: DJSEMTrackGraphNode, options: TrackGraphBuildOptions = DEFAULT_GRAPH_OPTIONS): DJSEMTrackGraphEdge {
    const reasons: DJSEMGraphEdgeReason[] = [];
    const warnings: string[] = [];
    const parts: string[] = [];
    let score = 0, maxScore = 0;

    // BPM
    maxScore += options.bpmWeight;
    if (typeof from.bpm === "number" && typeof to.bpm === "number") {
      const diff = Math.abs(from.bpm - to.bpm);
      const pct = from.bpm > 0 ? diff / from.bpm : 1;
      const prox = clampUnit(1 - pct / 0.16);
      score += prox * options.bpmWeight;
      if (prox > 0.5) { reasons.push("bpm_close"); parts.push(`BPM ${from.bpm}→${to.bpm} close`); }
      if (diff > 16 || pct > 0.16) { warnings.push(`Large BPM jump ${from.bpm}→${to.bpm} (Δ${diff.toFixed(0)}).`); reasons.push("warning"); }
    } else {
      warnings.push("Missing BPM; BPM compatibility unknown.");
    }

    // Key
    maxScore += options.keyWeight;
    const keyC = camelotCompatibility(from.key, to.key);
    if (keyC === null) {
      warnings.push("Missing/unreadable key; harmonic match unknown.");
      if (!reasons.includes("warning")) reasons.push("warning");
    } else {
      score += keyC * options.keyWeight;
      if (keyC >= 0.7) { reasons.push("key_compatible"); parts.push(`keys ${from.key}/${to.key} compatible`); }
    }

    // Energy flow
    maxScore += options.energyWeight;
    if (typeof from.energy === "number" && typeof to.energy === "number") {
      const d = Math.abs(from.energy - to.energy);
      const flow = clampUnit(1 - d);
      score += flow * options.energyWeight;
      if (d <= 0.2) { reasons.push("energy_flow"); parts.push("smooth energy flow"); }
      else if (d >= 0.5) { warnings.push(`Energy jump ${this.fmt(from.energy)}→${this.fmt(to.energy)}.`); }
    }

    // Mood continuity
    maxScore += options.moodWeight;
    const moodOverlap = this.overlap(from.moodTags, to.moodTags);
    if (moodOverlap > 0) { score += clampUnit(moodOverlap) * options.moodWeight; reasons.push("mood_continuity"); parts.push("shared mood"); }

    // Semantic continuity
    maxScore += options.semanticWeight;
    const semOverlap = this.overlap(from.semanticDomains, to.semanticDomains) + this.overlap(from.semanticCategories, to.semanticCategories);
    if (semOverlap > 0) { score += clampUnit(semOverlap / 2) * options.semanticWeight; reasons.push("semantic_continuity"); parts.push("semantic continuity"); }

    // Approval / readiness
    maxScore += options.approvalWeight;
    if (to.approvedForLive) score += options.approvalWeight;
    else warnings.push(`${to.title} is not approved for live.`);
    for (const w of to.warnings ?? []) warnings.push(`${to.title}: ${w}`);

    const norm = maxScore > 0 ? clampUnit(score / maxScore) : 0;
    if (reasons.length === 0) reasons.push("warning");

    return {
      id: `edge_${from.preparationTrackId}__${to.preparationTrackId}`,
      fromPreparationTrackId: from.preparationTrackId,
      toPreparationTrackId: to.preparationTrackId,
      compatibilityScore: round(norm),
      transitionRisk: this.risk(norm, warnings.length),
      reasons: dedupe(reasons),
      explanation: parts.length ? cap(parts.join(", ")) + "." : "No strong compatibility signal.",
      warnings,
      direction: options.directed ? "directed" : "undirected",
    };
  }

  /**
   * Narrow a graph to nodes/edges matching a set of filters.
   * @param graph - The graph to filter.
   * @param filters - The node- and edge-level filters to apply.
   * @returns A new graph with only matching nodes and edges (input is not mutated).
   */
  filterGraph(graph: DJSEMTrackRelationshipGraph, filters: DJSEMTrackGraphFilter[]): DJSEMTrackRelationshipGraph {
    let nodes = graph.nodes;
    for (const f of filters) {
      nodes = nodes.filter((n) => this.nodePassesFilter(n, f));
    }
    const keep = new Set(nodes.map((n) => n.preparationTrackId));
    let edges = graph.edges.filter((e) => keep.has(e.fromPreparationTrackId) && keep.has(e.toPreparationTrackId));
    for (const f of filters) {
      if (f.kind === "min_compatibility") edges = edges.filter((e) => e.compatibilityScore >= (f.min ?? 0));
      if (f.kind === "transition_risk" && f.value) edges = edges.filter((e) => e.transitionRisk === f.value);
    }
    return { ...graph, nodes, edges, filters: [...graph.filters, ...filters] };
  }

  /**
   * Validate a graph's shape and internal consistency.
   * @param graph - The candidate graph to validate.
   * @returns The validation result.
   */
  validateGraph(graph: unknown): DJSEMValidationResult {
    return toDJSEMValidationResult(vTrackRelationshipGraph(graph), "track_graph");
  }

  // ---- private --------------------------------------------------------------
  private nodePassesFilter(n: DJSEMTrackGraphNode, f: DJSEMTrackGraphFilter): boolean {
    switch (f.kind) {
      case "approved_only": return n.approvedForLive === true;
      case "bpm_range": return typeof n.bpm === "number" && n.bpm >= (f.min ?? -Infinity) && n.bpm <= (f.max ?? Infinity);
      case "energy_range": return typeof n.energy === "number" && n.energy >= (f.min ?? 0) && n.energy <= (f.max ?? 1);
      case "mood": return n.moodTags.includes(String(f.value));
      case "semantic_domain": return n.semanticDomains.includes(String(f.value));
      case "cue_intent": return n.semanticCategories.includes(String(f.value)); // cue proxy on node projection
      case "genre": return n.semanticCategories.includes(String(f.value)) || (n.moodTags.includes(String(f.value)));
      case "min_compatibility":
      case "transition_risk":
      case "chapter_fit":
      default: return true; // edge-level or non-node filters don't remove nodes
    }
  }
  private risk(score: number, warnCount: number): DJSEMTrackGraphEdge["transitionRisk"] {
    if (warnCount >= 2 && score < 0.4) return "high";
    if (score >= 0.66) return "low";
    if (score >= 0.4) return "medium";
    if (score <= 0) return "unknown";
    return "high";
  }
  private overlap(a: string[], b: string[]): number {
    if (!a?.length || !b?.length) return 0;
    const setB = new Set(b);
    return a.filter((x) => setB.has(x)).length;
  }
  private fmt(n: number): string { return n.toFixed(2); }
}

function dedupe<T>(xs: T[]): T[] { return [...new Set(xs)]; }
function cap(s: string): string { return s.charAt(0).toUpperCase() + s.slice(1); }
